import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useBaseAction } from "hooks/core";
import useAvailableFeaturesManagement from "hooks/tenant/useAvailableFeaturesManagement";
import DataTable from "components/common/DataTable/DataTable";
import FeaturesManagementTableColumns from "constants/tables/featuresTableCoulumns";
import { IActiveFeatureData, IFeatureData } from "components/features/types";
import { API } from "constants/RestApi";
import { toast } from "react-toastify";
import AddEditModalConfig from "./AddEditConfigModal";

const FeaturesTab = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { request } = useBaseAction();
  const [features, setFeatures] = useState<IFeatureData[] | []>([]);
  const [selectedFeature, setSelectedFeature] = useState<IFeatureData>();
  const { availableFeatures } = useAvailableFeaturesManagement({
    fetchOnLoad: false,
  });

  const fetchActiveFeatures = async () => {
    const params = {
      filter: `tenantId eq ${tenantId}`,
    };
    try {
      const response = await request({
        method: "GET",
        url: `${API.ADMIN_FEATURES_ACTIVE}`,
        params,
        useLoader: true,
      });

      const activeFeaturesMap = new Map<string, IActiveFeatureData>(
        (response as IActiveFeatureData[]).map((activeFeature) => [
          activeFeature.featureId,
          activeFeature,
        ]),
      );

      const responseFeatures = availableFeatures.map((availableFeature) => {
        const activeFeature = activeFeaturesMap.get(availableFeature.featureId);
        return {
          featureId: availableFeature.featureId,
          name: availableFeature.name,
          capabilities: availableFeature?.capabilities || [],
          id: activeFeature?.id,
          rowVersion: activeFeature?.rowVersion,
          configuration: activeFeature?.configuration,
          configName: activeFeature?.configuration?.name,
          configId: activeFeature?.configuration?.id,
          isEnabled: activeFeature?.isEnabled,
          isActive: activeFeature?.isActive ?? false,
        };
      });
      setFeatures(responseFeatures);
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch features");
    }
  };

  const toggleFeatureActive = async (
    data: IFeatureData,
    id: string,
    rowVersion: string,
    isEnabled: boolean,
  ) => {
    if (id) {
      await request({
        method: "PUT",
        url: API.ADMIN_FEATURES_ACTIVE,
        useLoader: true,
        body: {
          id,
          rowVersion,
          isEnabled: !isEnabled,
        },
        successMessage: isEnabled
          ? "Feature deactivated successfully"
          : "Feature activated successfully",
        errorMessage: "Something went wrong!",
      });
    } else if (!data.capabilities.includes("CanUseSharedConfigurations")) {
      await request({
        method: "POST",
        url: API.ADMIN_FEATURES_ACTIVE,
        useLoader: true,
        body: {
          tenantId,
          featureId: data.featureId,
        },
        successMessage: isEnabled
          ? "Feature deactivated successfully"
          : "Feature activated successfully",
        errorMessage: "Something went wrong!",
      });
    }
    await fetchActiveFeatures();
  };

  const closeModal = () => {
    setSelectedFeature(undefined);
  };

  const handleAddEditConfig = async (
    data: IFeatureData & {
      isEnabled: boolean;
      isActive: boolean;
      configType: string;
      configTypeChanged?: boolean;
      previousConfigType?: string;
    },
    isEditMode: boolean,
    callback?: () => void,
  ) => {
    // Handle feature activation/deactivation
    if (data.configId && !data.configTypeChanged && data?.isActive) {
      // Update existing feature
      await request({
        method: "PUT",
        url: API.ADMIN_FEATURES_ACTIVE,
        useLoader: true,
        body: {
          id: selectedFeature?.id,
          rowVersion: selectedFeature?.rowVersion,
          isEnabled: data.isEnabled,
        },
        successMessage: data.isEnabled
          ? "Feature activated successfully"
          : "Feature deactivated successfully",
        errorMessage: "Something went wrong!",
      });
      await fetchActiveFeatures();
      if (callback) callback();
      closeModal();
      return;
    }

    // Handle configuration type change - first delete existing config if type changed
    if ((data.configTypeChanged && selectedFeature?.configId) || !data?.isActive) {
      // First, delete the existing configuration
      await request({
        method: "DELETE",
        url: API.ADMIN_FEATURES_ACTIVE,
        useLoader: true,
        body: {
          id: data?.id,
          rowVersion: selectedFeature?.rowVersion,
        },
        successMessage: "Previous configuration removed",
        errorMessage: "Failed to remove previous configuration",
      });

      if (data?.isActive) {
        // Handle new configuration assignment
        const response = await request({
          method: "POST",
          url: API.ADMIN_FEATURES_ACTIVE,
          useLoader: true,
          body: {
            tenantId,
            featureId: selectedFeature?.featureId,
            ...(data?.configuration?.value &&
              data?.configType === "shared" && {
                sharedConfigurationId: data?.configuration?.value,
              }),
          },
          successMessage: "Shared configuration assigned successfully",
          errorMessage: "Failed to assign shared configuration",
        });
        if (data?.isActive && !data?.isEnabled) {
          await request({
            method: "PUT",
            url: API.ADMIN_FEATURES_ACTIVE,
            useLoader: true,
            body: {
              id: response?.id,
              rowVersion: response?.rowVersion,
              isEnabled: data.isEnabled,
            },
          });
        }
      }

      await fetchActiveFeatures();
      if (callback) callback();
      closeModal();
      return;
    }

    // Handle new configuration assignment
    const response = await request({
      method: "POST",
      url: API.ADMIN_FEATURES_ACTIVE,
      useLoader: true,
      body: {
        tenantId,
        featureId: selectedFeature?.featureId,
        ...(data?.configuration?.value &&
          data?.configType === "shared" && {
            sharedConfigurationId: data?.configuration?.value,
          }),
      },
      successMessage: "Shared configuration assigned successfully",
      errorMessage: "Failed to assign shared configuration",
    });
    if (data?.isActive && !data?.isEnabled) {
      await request({
        method: "PUT",
        url: API.ADMIN_FEATURES_ACTIVE,
        useLoader: true,
        body: {
          id: response?.id,
          rowVersion: response?.rowVersion,
          isEnabled: data.isEnabled,
        },
      });
    }
    await fetchActiveFeatures();
    if (callback) callback();
    closeModal();
  };

  useEffect(() => {
    if (!tenantId || !availableFeatures?.length) return;
    fetchActiveFeatures();
  }, [availableFeatures, tenantId]);

  return (
    <div className="col-12 mx-auto mb-5">
      <div className="card shadow-lg">
        <DataTable
          tableData={features}
          tableColumns={FeaturesManagementTableColumns({
            handleCheckboxAction: toggleFeatureActive,
            onEditConfig: setSelectedFeature,
          })}
          tableClassName="table-striped mb-0"
          classContainer="table-responsive-xl"
        />
      </div>
      <AddEditModalConfig
        feature={selectedFeature || ({} as IFeatureData)}
        showModal={!!selectedFeature}
        onCancel={closeModal}
        onClose={closeModal}
        onSave={(modalData, isEditMode, callback) =>
          handleAddEditConfig(modalData! as any, isEditMode!, callback)
        }
      />
    </div>
  );
};

export default FeaturesTab;
